import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { fetchPlatformBroadcastState, type BroadcastState } from "../../api/platformBroadcasts";
import { DevOpsFloatingMenu } from "./DevOpsFloatingMenu";
import { GlobalNoticesBar } from "./GlobalNoticesBar";
import { PlatformBroadcastContext } from "./PlatformBroadcastContext";
import { UpdatingOverlay } from "./UpdatingOverlay";
import { isPlatformOpsUser } from "./constants";
import { RoleViewOverrideProvider } from "../../contexts/RoleViewOverrideContext";
import { usePolling } from "../../hooks/usePolling";

const POLL_MS = 45_000;

export default function PlatformBroadcastWrapper() {
  const accessToken = useSelector((s: RootState) => s.session.accessToken);
  const sessionLoading = useSelector((s: RootState) => s.session.loading);
  const userId = useSelector((s: RootState) => s.session.currentUser?.id);
  const email = useSelector(
    (s: RootState) => s.session.currentUser?.email as string | undefined
  );

  const [state, setState] = useState<BroadcastState | null>(null);
  const [loading, setLoading] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);

  const refresh = useCallback(async () => {
    if (!accessToken) {
      setState(null);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchPlatformBroadcastState();
      setState(data);
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  usePolling(refresh, POLL_MS, { enabled: Boolean(accessToken) });

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) {
      setBarHeight(0);
      return;
    }
    const measure = () => setBarHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state]);

  const inProgress = state?.scheduled_update?.phase === "in_progress";
  // Dev-meny: kun fullt innlogget økt (token + bruker lasta frå API) og eksakt ops-e-post.
  const showOps =
    !sessionLoading &&
    Boolean(accessToken) &&
    Boolean(userId) &&
    isPlatformOpsUser(email);

  return (
    <RoleViewOverrideProvider>
      <PlatformBroadcastContext.Provider value={{ state, refresh, loading }}>
        <GlobalNoticesBar ref={barRef} state={state} />
        {inProgress && state?.scheduled_update ? (
          <UpdatingOverlay
            scheduledUpdate={state.scheduled_update}
            showAdminHint={showOps}
          />
        ) : null}
        {showOps ? <DevOpsFloatingMenu /> : null}
        <div
          style={{
            paddingTop: inProgress ? 0 : barHeight,
          }}
        >
          <Outlet />
        </div>
      </PlatformBroadcastContext.Provider>
    </RoleViewOverrideProvider>
  );
}
